/**
 * The wording that changes with a project's scheduling method.
 *
 * An AVAILABILITY_BASED project asks for "tijdvakken" rather than "diensten", because the actual
 * shift times are not known yet — saying "shift" there promises something the planner has not
 * decided. Keeping that choice in one place is what stops the two halves of a screen from
 * disagreeing with each other.
 *
 * Every function takes `t` rather than calling `useTranslation` itself: these are plain functions
 * used inside render, inside toasts and inside a test, and only the first of those can host a
 * hook.
 */

/**
 * What these functions need from a translator, and nothing more.
 *
 * Not i18next's `TFunction`: that type is branded with the namespace it was created for, so a
 * caller that did `useTranslation(["custom-project-detail"])` could not pass its own `t` without a
 * cast. These functions do not care which namespace the caller opened — they pass fully-qualified
 * keys either way.
 */
type Translate = (key: string, options?: Record<string, unknown>) => string;

type Project = { signupIsScheduling?: boolean; schedulingMethod?: string } | null | undefined;

export function getShiftTerminology(
  t: Translate,
  schedulingMethod: string | undefined,
  count: number = 1,
): string {
  const kind = schedulingMethod === "AVAILABILITY_BASED" ? "timeslot" : "shift";
  return t(`common:scheduling.terminology.${kind}-${count === 1 ? "one" : "other"}`);
}

export function getSignupTitle(t: Translate, schedulingMethod: string | undefined): string {
  return schedulingMethod === "AVAILABILITY_BASED"
    ? t("common:scheduling.title.availability")
    : t("common:scheduling.title.shift");
}

export function getSignupDescription(t: Translate, schedulingMethod: string | undefined): string {
  return schedulingMethod === "AVAILABILITY_BASED"
    ? t("common:scheduling.description.availability")
    : t("common:scheduling.description.shift");
}

/**
 * Issue #424 — "aanmelden = inplannen". True when a signup in the app directly schedules the
 * employee onto the matching shift instead of creating a pending application. That is only the case
 * for SHIFT_BASED projects with `signupIsScheduling` on; every other combination (flag off,
 * AVAILABILITY_BASED, or no project loaded) keeps the regular pending/reserve flow.
 */
export function isDirectScheduling(project: Project): boolean {
  return Boolean(project?.signupIsScheduling) && project?.schedulingMethod === "SHIFT_BASED";
}

/**
 * Toast shown after a successful signup. A direct-scheduling project (#424) confirms the scheduling
 * rather than merely acknowledging the application — the difference matters to whoever now has to
 * show up.
 */
export function getSignupSuccessMessage(t: Translate, project: Project): string {
  return isDirectScheduling(project)
    ? t("common:scheduling.success.direct")
    : t("common:scheduling.success.pending");
}

/** Body text for the "what happens next" confirmation section. */
export function getSignupNextStepText(t: Translate, project: Project): string {
  return isDirectScheduling(project)
    ? t("common:scheduling.next-step.direct")
    : t("common:scheduling.next-step.pending");
}
